import React, { useState, useRef, useEffect } from 'react';

import type { AxiosResponse } from 'axios';

import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import { ISecondarySelectOption } from 'components/shared/BaseFormComponents/Select/types';
import Modal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import Loader from 'components/shared/Loader';
import axiosApi from 'utils/axios';
import { ITINERARIES_ITEM_TYPE_TO_ROUTE_SEGMENT_MAP } from 'utils/itineraries/functions';
import logger from 'utils/logger';

import { BASE_DOCX_TEMPLATES } from './constants';
import { IProps, IDocxTheme, EtagsRef } from './types';

const DocxTemplateForm = ({ onSubmit, onClose, proposalType, proposalId, accountId }: IProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<ISecondarySelectOption[]>([]);
  const [theme, setTheme] = useState<string>();
  const etagsRef = useRef<EtagsRef>({});

  const handleChange = (id: string | null) => {
    if (id) {
      setTheme(id);
    }
  };

  const handleSubmit = () => {
    if (theme) {
      onSubmit(theme, etagsRef.current[theme]);
    }
    onClose();
  };

  const fetchThemes = async () => {
    try {
      setIsLoading(true);
      const routeSegment = ITINERARIES_ITEM_TYPE_TO_ROUTE_SEGMENT_MAP[proposalType];
      const url = `acc/${accountId}/${routeSegment}/${proposalId}/files/docx/themes`;
      const { data }: AxiosResponse<IDocxTheme[], any> = await axiosApi.get(url);

      if (data && Array.isArray(data)) {
        return data;
      }

      return [];
    } catch (err) {
      logger.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const convertThemesToOptions = (themes: IDocxTheme[]) => {
    return themes.map((t) => {
      etagsRef.current[t.themeId] = t.etag;

      return {
        label: t.displayName,
        value: t.themeId,
      };
    });
  };

  const setDefaultTheme = (themes: IDocxTheme[]) => {
    themes.forEach((t) => {
      if (t.displayName === BASE_DOCX_TEMPLATES.LANDSCAPE) {
        setTheme(t.themeId);
      }
    });
  };

  useEffect(() => {
    fetchThemes()
      .then((themes?: IDocxTheme[]) => {
        if (themes) {
          setDefaultTheme(themes);
          setOptions(convertThemesToOptions(themes));
        }
      })
      .catch(logger.error);
  }, []);

  return (
    <Modal
      headerText="Download Word Doc"
      bodyText={
        <div data-testid="wordThemeFormWrapper">
          {isLoading ? (
            <Loader />
          ) : (
            <>
              <p style={{ marginBottom: '16px' }}>Select Word Template</p>
              <BaseSelect placeholder="Select" onChange={handleChange} value={theme} options={options} />
            </>
          )}
        </div>
      }
      res={onClose}
      confirmButtonText="Select"
      onAccept={handleSubmit}
      isDisabledAcceptBtn={!theme}
      hideCloseButton
    />
  );
};

export default DocxTemplateForm;
